import { ChangeEvent, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useFeatureFlagEnabled } from "posthog-js/react"

import { CaseNumberDetails } from "./CaseNumberDetails"
import { AppDispatch } from "../../../../reduxStore/store"
import {
  fetchReferralPacket,
  resetState,
  selectReferralPacket,
} from "../../../../domain/Referral/loader"
import { CACHE_KEY_CASE_ID } from "../../../../domain/Account/userSliceCacheKeys"
import { clearHandoffCaseLocalStorage } from "../../utils"
import { useCMSReady } from "../../../../domain/CMS/useCMSReady"
import { apiService } from "../../apiService"
import { ReferralStats } from "../../../../domain/Referral/schema"
import {
  selectIsAROrEapResearch,
  selectIsCareManager,
} from "../../../../domain/Account/userSlice"
import ExpandableCard from "../ExpandableCard"
import { Input } from "../Input"
import Button from "../Button"
import { FeatureFlag } from "../../../../domain/enums/featureFlags"
import Spinner from "../Spinner"
import Label from "../Label"
import { Typography, typographyTypes } from "../Typography"

interface CaseInformationProps {
  sending: boolean
  handleSessionLimitChange: (sessionLimit: string) => void
  showReferralSessionLimitError: boolean
  caseSessionLimit: number
  onReferralSessionLimitChange?: (referralSessionLimit: number) => void
}

export const CaseInformation: React.FC<CaseInformationProps> = ({
  sending,
  handleSessionLimitChange,
  showReferralSessionLimitError,
  caseSessionLimit,
  onReferralSessionLimitChange,
}) => {
  const isAROrEapResearch = useSelector(selectIsAROrEapResearch)
  const isCareManager = useSelector(selectIsCareManager)
  const canOverrideSessionLimit = isAROrEapResearch && isCareManager
  const handoffCaseId = localStorage.getItem(CACHE_KEY_CASE_ID)
  const [caseNumber, setCaseNumber] = useState(handoffCaseId || "")
  const [error, setError] = useState<string | null>(null)
  const dispatch: AppDispatch = useDispatch()

  const isMemberIntakeCaseInfo19985Enabled =
    useFeatureFlagEnabled(FeatureFlag.IsMemberIntakeCaseInfo19985Enabled) ??
    false

  const {
    referralPacket,
    loading,
    error: apiErrorDetails,
  } = useSelector(selectReferralPacket)

  const { cms: cmsContent } = useCMSReady(["requestReferralModal", "common"])
  const caseNumberContent = cmsContent?.requestReferralModal
  const commonText = cmsContent?.common
  const [referralStatsList, setReferralStatsList] = useState<ReferralStats[]>(
    [],
  )
  const [referralSessionLimit, setReferralSessionLimit] = useState<string>("")
  const [showAdjustSessionLimitAccordion, setShowAdjustSessionLimitAccordion] =
    useState<boolean>(false)

  useEffect(() => {
    if (!referralPacket) {
      setReferralStatsList([])
    } else {
      const fetchReferralStats = async () => {
        const referralStats = await apiService.referral.get.REFERRAL_STATS(
          referralPacket.caseID.toString(),
        )
        setReferralStatsList(referralStats)
      }
      fetchReferralStats()
    }
  }, [referralPacket])

  useEffect(() => {
    const totalSessionsCompleted = referralStatsList.reduce(
      (acc, stats) =>
        stats.referralStatusDisplayId === "closed"
          ? acc + stats.completedSessionCount
          : acc,
      0,
    )
    const totalSessionsScheduled = referralStatsList.reduce(
      (acc, stats) =>
        stats.referralStatusDisplayId === "closed"
          ? acc + stats.scheduledSessionCount
          : acc,
      0,
    )
    onReferralSessionLimitChange?.(
      caseSessionLimit - (totalSessionsCompleted + totalSessionsScheduled),
    )
  }, [referralStatsList, caseSessionLimit, onReferralSessionLimitChange])

  useEffect(() => {
    if (apiErrorDetails) {
      if (
        apiErrorDetails.statusCode === 409 // Conflict
      ) {
        setError(caseNumberContent?.activeCaseReferralExists || "")
      } else if (
        apiErrorDetails.statusCode === 403 // Forbidden
      ) {
        setError(caseNumberContent?.caseNumberSecureCaseUserRoleError || "")
      } else if (
        apiErrorDetails.statusCode === 404 // Not Found
      ) {
        setError(caseNumberContent?.caseNumberValidCaseNumberError || "")
      } else {
        setError(
          apiErrorDetails.errorDetails.errorMessage ||
            caseNumberContent?.caseNumberCannotRetrieveError ||
            "",
        )
      }
    } else {
      if (referralPacket) {
        setError(null)
      }
    }
  }, [apiErrorDetails, caseNumberContent, referralPacket])

  useEffect(() => {
    return () => {
      dispatch(resetState())
    }
  }, [dispatch])

  useEffect(() => {
    if (showReferralSessionLimitError && !showAdjustSessionLimitAccordion) {
      setShowAdjustSessionLimitAccordion(true)
    }
  }, [showReferralSessionLimitError, showAdjustSessionLimitAccordion])

  const [isDirty, setIsDirty] = useState(false)

  const validate = (value: string): string | null => {
    if (value.trim() === "") {
      return caseNumberContent?.caseNumberRequiredFieldError || ""
    }

    if (!/^\d{5,9}$/.test(value)) {
      return caseNumberContent?.caseNumberMinMaxValidationError || ""
    }
    return null
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.replace(/[^0-9]/g, "")
    setCaseNumber(value)
    if (isDirty) {
      const validationError = validate(value)
      setError(validationError)
    }
  }

  const handleSubmit = () => {
    setIsDirty(true)
    const validationError = validate(caseNumber)
    if (validationError) {
      setError(validationError)
      return
    }
    if (handoffCaseId && handoffCaseId !== caseNumber) {
      clearHandoffCaseLocalStorage()
    }
    dispatch(
      fetchReferralPacket({
        caseId: caseNumber,
        useCache: !!handoffCaseId && handoffCaseId === caseNumber,
        includeMemberIntake: isMemberIntakeCaseInfo19985Enabled,
      }),
    )
  }

  if (loading || sending) {
    return <Spinner parentStyleOverrides="h-fit pt-10" />
  }

  const CardComponent = ExpandableCard

  return (
    <>
      <div className="w-1/2 space-y-2">
        <Label
          text="Enter a case number for this referral:"
          htmlFor="case-number-input"
          isRequired={true}
          size="sm"
          fontWeight="medium"
        />
        <div className="flex space-x-4 w-full">
          <Input
            title=""
            onInputChange={handleInputChange}
            inputValue={caseNumber}
            inputId="case-number-input"
            doNotRenderTitle={true}
            styleOverrides="w-full"
            error={!!error}
            maxLengthErrorText={commonText?.characterLimit || ""}
          />
          <Button
            buttonLabel={
              caseNumberContent?.caseInformationRetrieveButtonLabel || ""
            }
            onClick={handleSubmit}
            type="primary"
            styleOverrides="w-auto"
            roundedFull={false}
          />
        </div>
      </div>
      {error && (
        <div role="alert" className="error-text mt-2">
          {error}
        </div>
      )}
      {!apiErrorDetails && !loading && referralPacket && (
        <div>
          <CaseNumberDetails
            referralPacket={referralPacket}
            referralStatsList={referralStatsList}
            caseSessionLimit={caseSessionLimit}
          />
          {canOverrideSessionLimit && (
            <div className="mt-[24px]">
              <CardComponent
                title={caseNumberContent?.adjustReferralSessionLimitLabel || ""}
                defaultOpen={showAdjustSessionLimitAccordion}
                isOpen={showAdjustSessionLimitAccordion}
                onToggle={() =>
                  referralSessionLimit !== "" && showReferralSessionLimitError
                    ? setShowAdjustSessionLimitAccordion(true)
                    : setShowAdjustSessionLimitAccordion(
                        !showAdjustSessionLimitAccordion,
                      )
                }
              >
                <div className="w-full h-full mt-1 mb-0 pl-[6px]">
                  <div className="w-full font-medium text-gray-700">
                    <Typography
                      text={
                        caseNumberContent?.adjustReferralSessionLimitText || ""
                      }
                      styleOverrides="text-base"
                      variant={typographyTypes.NORMAL}
                    />
                  </div>
                  <div className="w-full mt-2">
                    <Input
                      title=""
                      onInputChange={(event) => {
                        const value = event.target.value.replace(/[^0-9]/g, "")
                        handleSessionLimitChange(value)
                        setReferralSessionLimit(value)
                      }}
                      inputValue={referralSessionLimit?.toString() || ""}
                      inputId="new-session-limit-input"
                      doNotRenderTitle={true}
                      styleOverrides="w-[65px] h-[42px] p-0"
                      error={showReferralSessionLimitError}
                      errorText={
                        caseNumberContent?.referralSessionLimitMaxError || ""
                      }
                    />
                  </div>
                </div>
              </CardComponent>
            </div>
          )}
        </div>
      )}
    </>
  )
}
